<template>
  <CardBase v-if="table">
    <TableBase
      :table="table"
      :bodyHeight="isFeatureFullscreen ? 'auto' : '300px'"
      layout="fixed"
      rightTextAlign="center"
      :tabbableRows="true"
      caption="Ticket table"
      @mouseEnterCell="hoveredIndex = $event.cell.index"
      @mouseLeaveCell="hoveredIndex = null"
      @onClickRow="onClickRow"
      @nearEnd="nearEnd"
    >
      <template #header-cell="{ cell: { label } }">
        <TextBase :text="label?.title" textWrap="nowrap" :type="'paragraph'" :weight="'bold'" />
      </template>
      <template #body-cell="{ cell, cellIndex }">
        <div
          :style="{ cursor: 'pointer' }"
          @mouseenter="
            () => addHoveredItem(getTicketEntityId(cell.cell.ticket) ?? '', { color: '#fadb14' })
          "
          @mouseleave="removeLastHoveredItem"
        >
          <TooltipStandardV2
            :style="{ height: '100%' }"
            :text="cell.cell.label"
            heatmap
            arrow-position="top-center"
          >
            <TextBase
              v-if="cellIndex % 3 === 0 && cell.cell.label"
              :text="cell.cell.label"
              textWrap="nowrap"
              :type="'paragraph'"
              :weight="'regular'"
              :overflow="'ellipsis'"
            />
            <PulseLoader v-else-if="cellIndex % 3 === 0" :loading="true" color="#3F46D6" />
          </TooltipStandardV2>
          <div v-if="cellIndex % 3 === 1" class="last-cell">
            <BadgeSquare
              :badgeText="cell.cell.label"
              :size="'small'"
              :color="'primary'"
              :style="{ textAlign: 'left' }"
            />
            <SvgDisplay :src="'Icon-Arrow-Up-Right_x16'" />
          </div>
        </div>
      </template>
    </TableBase>
  </CardBase>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useGeneralStore } from '@/pinia'
import TableBase from '@/design-system/atoms/TableBase.vue'
import TextBase from '@/design-system/atoms/TextBase.vue'
import BadgeSquare from '@/design-system/atoms/BadgeSquare.vue'
import SvgDisplay from '@/design-system/atoms/SvgDisplay.vue'
import CardBase from '@/components/AnalyticsDashboard/components/CardBase.vue'
import { Table } from '@/design-system/atoms/table-base'
import TooltipStandardV2 from '@/design-system/atoms/TooltipStandardV2.vue'
import { useFetchTickets } from '@/api-client/models/tickets/useFetchTickets'
import PulseLoader from 'vue-spinner/src/PulseLoader.vue'
import { Ticket } from '@/api-client/models/tickets/Contracts'
import { getSingleTicket } from '@/services/tickets.service'
import { useHoverStore } from '@/pinia/hover'

type HeaderCell = {
  label: {
    title: string
    subtitle?: string
  }
}

type BodyCell = {
  cell: {
    index: number
    label: string
    id?: string
    badgeColor?: string
    backgroundColor?: string
    ticket: Ticket
  }
}

type ProjectTable = Table<HeaderCell, BodyCell>

const piniaStore = useGeneralStore()

const hoveredIndex = ref<number | null>(null)
const showDetails = ref(false)
const selectedTicket = ref<Ticket | undefined>(undefined)
const { t } = useI18n()

const { itemList: filteredItems, loadMore, titleMap, totalTickets } = useFetchTickets()
const { addHoveredItem, removeLastHoveredItem } = useHoverStore()

const isFeatureFullscreen = computed(() => {
  return piniaStore.isFeatureFullscreen
})

const table = computed<ProjectTable>((): ProjectTable => {
  const headers: ProjectTable['headers'] = {
    top: [
      {
        label: {
          title: `${totalTickets.value} Tickets`
        },
        style: {
          width: '40%',
          height: '32px',
          padding: '4px',
          'background-color': '#D5D7F666'
        }
      },
      {
        label: {
          title: 'Typ'
        },
        style: {
          width: '35%',
          height: '32px',
          padding: '4px',
          'text-align': 'left',
          'background-color': '#D5D7F666'
        }
      }
    ]
  }
  const body: ProjectTable['body'] = filteredItems.value.map((ticket, index) => {
    return [
      {
        cell: {
          index: index,
          label: titleMap.value[ticket.ticketId],
          ticket
        },
        style: {
          width: '40%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      },
      {
        cell: {
          index: index,
          label: t(ticket.typeId),
          ticket
        },
        style: {
          width: '35%',
          padding: '4px',
          backgroundColor: hoveredIndex.value === index ? '#CDDEFE33' : 'transparent',
          cursor: 'pointer'
        }
      }
    ]
  })
  return {
    headers,
    body
  }
})

watch(selectedTicket, async (ticket) => {
  if (ticket) {
    const fullTicket = await getSingleTicket(ticket.ticketId)
    if (fullTicket) {
      piniaStore.setSelectedTicket(fullTicket)
      piniaStore.setCurrentOverlayModalPinia('editTicket')
      piniaStore.setShowOverlayModalPinia(true)
      selectedTicket.value = undefined
    }
  }
})

function onClickRow(row?: BodyCell[]) {
  if (!row || !row?.[1]?.cell.ticket) {
    return
  }
  selectedTicket.value = row?.[1].cell.ticket
  showDetails.value = true
}

function nearEnd(edge: 'top' | 'bottom') {
  if (edge === 'bottom') {
    loadMore()
  }
}

function getTicketEntityId(ticket: Ticket): string | undefined {
  if (ticket.spaceId) return ticket.spaceId
  if (ticket.levelId) return ticket.levelId
  if (ticket.constructionId) return ticket.constructionId
  return undefined
}
</script>

<style lang="scss" scoped>
@use '@/design-system/design-tokens/variables.scss';
@use '@/design-system/design-tokens/font-mixins';

button {
  all: unset;
  padding: 4px;
  &:hover {
    border-radius: 4px;
    background-color: aliceblue;
  }
}
.button-row {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

:deep(*) {
  #compensator {
    background-color: #d5d7f666;
  }
}

.last-cell {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 100%;
  cursor: pointer;
}
</style>
